import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const REFRESH_MS = 5 * 60 * 1000;
const SIGNED_TTL = 3600;

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function formatMinutes(m) {
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}m`;
}

export default function LiveView({ childName = 'Your Child', kidId, parentId, plan, features, deviceFilter }) {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [device, setDevice] = useState(null);
  const [screenTime, setScreenTime] = useState(0);
  const [currentApp, setCurrentApp] = useState(null);
  const [siteCount, setSiteCount] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const [screenshot, setScreenshot] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    if (!parentId) { setLoading(false); return; }
    loadAll();
    const interval = setInterval(loadAll, REFRESH_MS);
    return () => clearInterval(interval);
  }, [parentId, kidId, deviceFilter]); // eslint-disable-next-line react-hooks/exhaustive-deps

  async function loadAll() {
    if (!parentId) return;
    setProgress(10);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      // Scope a query by parent_id and (if selected) kid_id.
      const scope = (q) => {
        q = q.eq('parent_id', parentId);
        if (kidId) q = q.eq('kid_id', kidId);
        if (deviceFilter && deviceFilter !== 'all') q = q.eq('source', deviceFilter);
        return q;
      };

      setProgress(20);
      const [
        { data: devices },
        { data: usage },
        { data: latestApp },
        { count: sites },
        { count: alerts },
        { data: screenshots },
        { data: recentApps },
        { data: recentSites },
      ] = await Promise.all([
        scope(supabase.from('devices').select('*')).limit(1),
        scope(supabase.from('app_usage').select('duration_seconds')).gte('timestamp', todayISO),
        scope(supabase.from('app_usage').select('app_name, timestamp')).order('timestamp', { ascending: false }).limit(1),
        scope(supabase.from('websites').select('*', { count: 'exact', head: true })).gte('timestamp', todayISO),
        scope(supabase.from('alerts').select('*', { count: 'exact', head: true })).eq('is_read', false),
        scope(supabase.from('screenshots').select('*')).order('timestamp', { ascending: false }).limit(1),
        scope(supabase.from('app_usage').select('id, app_name, timestamp')).order('timestamp', { ascending: false }).limit(5),
        scope(supabase.from('websites').select('id, title, url, timestamp')).order('timestamp', { ascending: false }).limit(5),
      ]);
      setProgress(70);

      // Device status
      if (devices?.[0]) {
        const lastSeen = new Date(devices[0].last_seen_at);
        const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
        setDevice({ ...devices[0], online: lastSeen > fiveMinAgo });
      }

      // Screen time
      const totalSeconds = (usage || []).reduce((sum, r) => sum + (r.duration_seconds || 0), 0);
      setScreenTime(Math.round(totalSeconds / 60));

      // Current app
      setCurrentApp(latestApp?.[0] || null);

      // Counts
      setSiteCount(sites || 0);
      setAlertCount(alerts || 0);
      setProgress(80);

      // Screenshot
      if (screenshots?.[0]) {
        setScreenshot(screenshots[0]);
        const { data } = await supabase.storage
          .from('parentguard')
          .createSignedUrl(screenshots[0].image_url, SIGNED_TTL);
        setSignedUrl(data?.signedUrl || null);
      } else {
        setScreenshot(null);
        setSignedUrl(null);
      }
      setProgress(90);

      // Combined recent activity
      const apps = (recentApps || []).map(a => ({
        id: a.id, type: 'app', label: a.app_name, timestamp: a.timestamp,
      }));
      const sites2 = (recentSites || []).map(s => ({
        id: s.id, type: 'site', label: s.title || s.url, timestamp: s.timestamp,
      }));
      const combined = [...apps, ...sites2]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 5);
      setRecentActivity(combined);
      setProgress(100);
    } catch (err) {
      console.error('LiveView load error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-icon">{'\uD83D\uDEE1\uFE0F'}</div>
        <div className="loading-title">Loading dashboard...</div>
        <div className="loading-bar-track">
          <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="loading-percent">{progress}%</div>
      </div>
    );
  }

  const isOnline = device?.online;

  return (
    <div>
      {/* Device connection banner */}
      <div className={`status-banner ${device ? (isOnline ? 'online' : 'offline') : 'none'}`}>
        <div className="status-icon">
          {device ? (isOnline ? '🟢' : '🔴') : '💻'}
        </div>
        <div>
          <strong>{device ? device.device_name || 'Mac' : 'No device registered'}</strong>
          <div className="page-subtitle">
            {device
              ? (isOnline ? 'Online now' : `Last seen ${formatTime(device.last_seen_at)}`)
              : 'Install the agent on your child\'s Mac to get started'}
          </div>
        </div>
        {isOnline && <span className="badge badge-green" style={{ marginLeft: 'auto' }}>Connected</span>}
      </div>

      {/* Stat cards */}
      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-label">Screen Time Today</div>
          <div className="stat-value">{formatMinutes(screenTime)}</div>
          <div className="stat-sub">Since midnight</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Current App</div>
          <div className="stat-value" style={{ fontSize: 18 }}>
            {currentApp?.app_name || '—'}
          </div>
          <div className="stat-sub">
            {currentApp ? formatTime(currentApp.timestamp) : 'No data yet'}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Sites Today</div>
          <div className="stat-value">{siteCount}</div>
          <div className="stat-sub">Unique visits</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Unread Alerts</div>
          <div className={`stat-value ${alertCount > 0 ? 'red' : ''}`}>{alertCount}</div>
          <div className="stat-sub">{alertCount > 0 ? 'Check alerts tab' : 'All clear'}</div>
        </div>
      </div>

      {/* Screenshot + Recent Activity */}
      <div className="grid-main">
        {/* Latest screenshot */}
        <div className="card">
          <div className="card-title">Latest Screenshot</div>
          {signedUrl ? (
            <a href={signedUrl} target="_blank" rel="noreferrer">
              <img
                src={signedUrl}
                alt="Latest screenshot"
                style={{ width: '100%', borderRadius: 8, display: 'block' }}
              />
            </a>
          ) : (
            <div className="empty-state">
              <div className="icon">📸</div>
              <div className="desc">No screenshots captured yet</div>
            </div>
          )}
          {screenshot && (
            <div className="stat-sub" style={{ marginTop: 10 }}>
              Captured {formatTime(screenshot.timestamp)}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="card">
          <div className="card-title">Recent Activity</div>
          {recentActivity.length === 0 ? (
            <div className="empty-state">
              <div className="desc">No activity recorded yet</div>
            </div>
          ) : (
            recentActivity.map(item => (
              <div key={item.id} style={{
                padding: '8px 0',
                borderBottom: '1px solid var(--border-light)',
                fontSize: 13,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span>
                  {item.type === 'app' ? '📱' : '🌐'}{' '}
                  <strong>{item.label}</strong>
                </span>
                <span style={{ color: 'var(--muted)', fontSize: 11 }}>
                  {formatTime(item.timestamp)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Coming Soon Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
        border: '1px solid #fde68a', borderRadius: 12,
        padding: '14px 16px', marginTop: 20,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        width: '100%', boxSizing: 'border-box',
      }}>
        <span style={{ fontSize: 20 }}>🚀</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>Coming Soon</div>
          <div style={{ fontSize: 12, color: '#b45309' }}>
            Location tracking · App blocking · Remote lock · YouTube monitoring · Geofencing · Social media alerts · Windows & Android support
          </div>
        </div>
        <a href="/dashboard/upgrade" style={{
          fontSize: 12, fontWeight: 700, color: '#d97706',
          textDecoration: 'none', whiteSpace: 'nowrap',
        }}>
          Learn more →
        </a>
      </div>
    </div>
  );
}
